/** @jsxImportSource @emotion/react */
import _ from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { UNSAFE_LocationContext } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { getLocale, setLocale } from 'react-i18nify';
import { getReport } from '../../api/api';
import * as repr from '../../api/types';
import t from '../../localization/i18n';
import authService from '../api-authorization/AuthorizeService';
import AccountAllocation from '../ReportShared/AccountAllocation';
import AccountPerformance from '../ReportShared/AccountPerformance';
import Benefits from '../ReportShared/Benefits';
import ComparableFunds from '../ReportShared/ComparableFunds';
import Fees from '../ReportShared/Fees';
import FundPerformance from '../ReportShared/FundPerformance';
import FundSpecificDisclosures from '../ReportShared/FundSpecificDisclosures';
import GiaMarketingSection from '../ReportShared/GiaMarketingSection';
import InvestmentOverview from '../ReportShared/InvestmentOverview';
import ProgramDetails from '../ReportShared/ProgramDetails';
import CoverPage from './CoverPage';
import EndPage from './EndPage';
import FundProfilePage from './FundProfilePage';
import FundProfileTitlePage from './FundProfileTitlePage';
import ImportantInfoPage from './ImportantInfoPage';
import IntroductionPage from './IntroductionPage';
import reportStyles from './Report.styles';

// Safe hook that returns location only when inside a Router context (client-side).
// Returns null during SSR when no Router is present.
function useLocationSafe(): Location | null {
    const locationContext = useContext(UNSAFE_LocationContext);
    // locationContext is null when not inside a Router
    return locationContext?.location ?? null;
}
// import '../../styles/pdf.scss';

function getUrlParams(search: string) {
    // `search` is "" when no URL params, and "?foo=bar" when
    // there are params. First, we slice off the starting "?", then split
    // on "&" to get key/value pairs. Resulting array always has at least
    // one string in it.
    const urlParamsArray = search.slice(1).split('&');
    const urlParams = urlParamsArray.reduce((memo, s) => {
        const [k, v] = s.split('=');
        return { ...memo, [k]: v };
    }, {});
    return urlParams;
}

interface PropsShape {
    model?: repr.Report
    locale?: string
    reportId?: string
    clientId?: string
}

export default function Report(props: PropsShape) {
    const location = useLocationSafe();
    const urlParams = location?.search
        ? getUrlParams(location.search) as { locale: string, proposalId: string, clientId: string }
        : null;

    // When rendering through client-side app, this component will fetch
    // proposal data. When rendering via SSR, this component will receive a
    // model directly from props, and won't attempt to fetch data on its own.
    const [model, setModel] = useState(props.model);

    // Ensure we don't attempt to set app locale on every render. This is
    // relevant only when rendering report through client-side app -- when
    // rendering via SSR, this component only renders once.
    const [hasLocaleBeenSet, markLocaleAsSet] = useState(false);

    if (!hasLocaleBeenSet) {
        if (urlParams && urlParams.locale) {
            // The fact that we have url params means we're rendering this
            // through the client-side app!
            setLocale(urlParams.locale);
            markLocaleAsSet(true);
        } else if (props.locale) {
            // If there's a locale from props, we're probably rendering via CLI.
            setLocale(props.locale);
            markLocaleAsSet(true);
        }
    }

    useEffect(() => {
        if (urlParams && urlParams.proposalId) {
            const proposalId = urlParams.proposalId;
            const clientId = urlParams.clientId || null;
            
            async function fetchReport() {
                const authToken = await authService.getAccessToken();
                const locale = getLocale();
                const response = await getReport(authToken, proposalId, clientId, locale);
                setModel(response.data as repr.Report);
            }
            fetchReport();
        }
    }, [urlParams]);

    // If a valid client id is provided, it means we're producing an individual
    // report (and not a household report).
    const clientId = props.clientId || (urlParams ? urlParams.clientId : null);

    // Create a derived model without mutating the original state
    const displayModel = useMemo(() => {
        if (!model) return null;

        let workingModel = model;
        let client = null;

        if (clientId) {
            client = model.clients.find(c => clientId === c.clientId);

        // TODO: For now, just remove all the other clients from the model.
        // If we actually have other logic we need to do for indiv vs household,
        // we might need to do a flag or something instead of just removing
        // irrelevant individuals like this.            
        if (client) {
                workingModel = { ...model, clients: [client] };
            } else {
                throw new Error(`Could not find client with "${clientId}" in model`);
            }
        }

        // Add fullName to clients without mutation
        return {
            ...workingModel,
            clients: workingModel.clients.map(c => ({
                ...c,
                fullName: `${t(`salutations.${c.salutation}`)} ${c.firstName} ${c.lastName}`.trim()
            }))
        };
    }, [model, clientId]);

    // This Loading message only gets rendered in the client-side app, while
    // the model is being fetched. When rendering server-side, we always
    // create this component with the model already available.
    if (!displayModel) {
        return <p>Loading...</p>;
    }

    let proposalName = '';
    const client = clientId ? displayModel.clients[0] : null;

    if (displayModel.proposalType === 'Household' && !client) {
        proposalName = displayModel.proposalName;
    } else {
        proposalName = `${displayModel.clients[0].fullName}`;
    }

    // Check if report only contains GIA funds
    const isGiaReport = displayModel.includeGIA && !_.some(
        displayModel.clients,
        c => _.some(c.accounts, a => a.holdings.filter(h => !h.isGia).length !== 0)
    );

    const fundDisclosures = displayModel.fundProfiles.filter(fund => fund.fundSpecificDisclosures !== null);
    

    return (
        <article
            className="report"
            data-running-footer-text={t('report.pleaseReadImportant')}
            css={reportStyles}
        >
            <header className="running-header">
                <span className="proposal-type">{t('report.investmentProposal')}</span>
                <span className="proposal-name">{proposalName}</span>
            </header>

            <header className="running-header--inverse">
                <span className="proposal-type">{t('report.investmentProposal')}</span>
                <span className="proposal-name">{proposalName}</span>
            </header>

            <CoverPage
                proposalName={proposalName}
                advisorFirstName={displayModel.advisorFirstName}
                advisorLastName={displayModel.advisorLastName}
                report={displayModel}
            />
            <IntroductionPage report={displayModel} />
            <InvestmentOverview report={displayModel} />

            {!isGiaReport && (
                <FundPerformance report={displayModel} fundDisclosures={fundDisclosures} />
            )}

            <ComparableFunds report={displayModel} />
            <AccountPerformance report={displayModel} variant='pdf' />
            <AccountAllocation report={displayModel} variant='pdf' />

            {/* Pool Details */}
            {displayModel.fundProfiles.length > 0 && <FundProfileTitlePage report={displayModel} />}
            {displayModel.fundProfiles.map(f => (
                <FundProfilePage key={f._id} fund={f} productType={displayModel.productType} investmentType={displayModel.investmentType}/>
            ))}

            {displayModel.includeGIA && <GiaMarketingSection />}
            <Fees report={displayModel} />
            <ProgramDetails investmentType={displayModel.investmentType} productType={displayModel.productType} report={displayModel} variant="pdf"/>
            <Benefits report={displayModel} />
            {displayModel.fundProfiles.some(fund => fund.fundSpecificDisclosures !== null) && <FundSpecificDisclosures report={displayModel} />}
            {displayModel.investmentType === 'Seg' && <ImportantInfoPage report={displayModel} />}
            <EndPage report={displayModel} />
        </article>
    );
}
